import axios, { type InternalAxiosRequestConfig } from 'axios'
import { ENDPOINTS } from '@/constants/endpoints'
import { PUBLIC_PATHS, ROUTES } from '@/constants/routes'
import { STORAGE_KEYS } from '@/constants/auth'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const isPublicPath = (pathname: string): boolean =>
  PUBLIC_PATHS.includes(pathname) || pathname.startsWith(`${ROUTES.SUBSCRIBE}/`)

const isSubscriptionWizardPath = (pathname: string): boolean =>
  pathname === ROUTES.SUBSCRIBE || pathname.startsWith(`${ROUTES.SUBSCRIBE}/`)

const getAccessToken = (): string | null => localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
const getRefreshToken = (): string | null => localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)

const clearTokens = (): void => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
}

const redirectToLogin = (): void => {
  if (!isPublicPath(window.location.pathname)) {
    window.location.href = ROUTES.LOGIN
  }
}

// global axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// interceptor to add the access token to the request headers
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.url?.includes('token/')) {
      return config
    }
    if (config.url?.includes('auth/2fa/verify')) {
      return config
    }
    if (config.url?.includes('password-reset')) {
      return config
    }
    if (isSubscriptionWizardPath(window.location.pathname)) {
      return config
    }
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `JWT ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

// interceptor: on 401 try refresh once, then clear tokens and redirect
api.interceptors.response.use(
  response => response,
  async error => {
    const status = error.response?.status
    const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (status === 401) {
      const isRefreshRequest = config?.url?.includes('refresh')
      if (isRefreshRequest || config?._retry) {
        clearTokens()
        redirectToLogin()
        return Promise.reject(error)
      }
      const refresh = getRefreshToken()
      if (refresh) {
        try {
          const { data } = await api.post<{ access: string }>(ENDPOINTS.AUTH.REFRESH, { refresh })
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access)
          config._retry = true
          config.headers.Authorization = `JWT ${data.access}`
          return api(config)
        } catch {
          // refresh failed: clear and redirect
        }
      }
      clearTokens()
      redirectToLogin()
      return Promise.reject(error)
    }

    // 403 = Forbidden (authentified but not allowed): do not logout, let the caller handle the error
    return Promise.reject(error)
  }
)

export default api
